// All calls go to /api on the app's own origin; Caddy forwards them to the API service.
// The session cookie is first-party and HttpOnly, so this code never sees it.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
  }
}

// Called after every change that succeeds, so the menu counts follow along (see main.tsx).
let changed: () => void = () => {};
export function onChange(fn: () => void) {
  changed = fn;
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    headers: init.body !== undefined ? { "content-type": "application/json" } : {},
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.message ?? "Something went wrong. Please try again.",
      data?.error ?? "error",
    );
  }
  if ((init.method ?? "GET") !== "GET") changed();
  return data as T;
}

export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });

/** Copies text, with a fallback for browsers that block the clipboard API. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const t = document.createElement("textarea");
    t.value = text;
    t.style.position = "fixed";
    t.style.opacity = "0";
    document.body.append(t);
    t.select();
    const ok = document.execCommand("copy");
    t.remove();
    return ok;
  }
}

export function timeAgo(iso: string | null, now = Date.now()) {
  if (!iso) return "";
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  const future = s < 0;
  const a = Math.abs(s);
  const n =
    a < 90
      ? "just now"
      : a < 3600
        ? `${Math.round(a / 60)} minutes`
        : a < 86_400 * 1.5
          ? a < 5400
            ? "an hour"
            : `${Math.round(a / 3600)} hours`
          : a < 86_400 * 2
            ? "a day"
            : `${Math.round(a / 86_400)} days`;
  if (n === "just now") return n;
  if (!future && n === "a day") return "yesterday";
  return future ? `in ${n}` : `${n} ago`;
}

/** 1234 -> "1.2k", for likes and comments. */
export function compact(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

/** "1.2k likes · 34 comments", or null when Instagram didn't share them. */
export function engagement(p: { likes: number | null; comments: number | null }) {
  const parts = [
    p.likes != null ? `${compact(p.likes)} ${p.likes === 1 ? "like" : "likes"}` : null,
    p.comments != null
      ? `${compact(p.comments)} ${p.comments === 1 ? "comment" : "comments"}`
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}
